import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { Tags, Search, RefreshCw, Plus, Trash2, ChevronLeft, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SOURCES = [
  { value: "easy", label: "Easy" },
  { value: "crm_legacy", label: "CRM Legacy" },
  { value: "salesforce", label: "Salesforce" },
  { value: "manual", label: "Manual" },
];

const TYPES = [
  { value: "marca_code", label: "Cod Marca" },
  { value: "full_name", label: "Nume complet" },
  { value: "first_last_name", label: "Nume + Prenume" },
];

const AgentMappings = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({ source_system: "", identifier_type: "", agent_id: "", search: "" });
  const [createDialog, setCreateDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [form, setForm] = useState({ agent_id: "", source_system: "easy", identifier_type: "marca_code", raw_value: "", is_primary: false });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", "50");
      if (filters.source_system) params.set("source_system", filters.source_system);
      if (filters.identifier_type) params.set("identifier_type", filters.identifier_type);
      if (filters.agent_id) params.set("agent_id", filters.agent_id);
      if (filters.search) params.set("search", filters.search);
      const [mRes, aRes] = await Promise.all([
        axios.get(`${API}/agent-mappings?${params.toString()}`),
        axios.get(`${API}/agents?limit=500`),
      ]);
      setItems(mRes.data.data || []);
      setTotal(mRes.data.total || 0);
      setPages(mRes.data.pages || 1);
      setAgents(aRes.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la încărcarea aliasurilor");
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.agent_id || !form.raw_value) {
      toast.error("Selectează un agent și introdu valoarea brută");
      return;
    }
    try {
      await axios.post(`${API}/agents/${form.agent_id}/identifiers`, {
        source_system: form.source_system,
        identifier_type: form.identifier_type,
        raw_value: form.raw_value,
        is_primary: form.is_primary,
        confidence: 1.0,
      });
      toast.success("Alias creat");
      setCreateDialog(false);
      setForm({ agent_id: "", source_system: "easy", identifier_type: "marca_code", raw_value: "", is_primary: false });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la creare");
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      await axios.delete(`${API}/agents/${deleteDialog.agent_id}/identifiers/${deleteDialog.id}`);
      toast.success("Alias șters");
      setDeleteDialog(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la ștergere");
    }
  };

  const canManage = user?.role === "admin" || user?.role === "backoffice";

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Aliasuri Agenți</h1>
          <p className="text-slate-500 mt-1">
            Identificatori per sursă (Easy/CRM/Salesforce). Aliasurile sunt folosite la matching automat la importuri.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} data-testid="refresh-mappings">
            <RefreshCw className="h-4 w-4 mr-2" />Reîncarcă
          </Button>
          {canManage && (
            <Button onClick={() => setCreateDialog(true)} data-testid="new-mapping-btn">
              <Plus className="h-4 w-4 mr-2" />Alias Nou
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Caută după valoare brută, nume agent, cod Marca..."
              className="pl-10"
              value={filters.search}
              onChange={(e) => { setPage(1); setFilters({ ...filters, search: e.target.value }); }}
              data-testid="filter-search"
            />
          </div>
          <Select
            value={filters.source_system || "all"}
            onValueChange={(v) => { setPage(1); setFilters({ ...filters, source_system: v === "all" ? "" : v }); }}
          >
            <SelectTrigger data-testid="filter-source"><SelectValue placeholder="Sursă" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate sursele</SelectItem>
              {SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select
            value={filters.identifier_type || "all"}
            onValueChange={(v) => { setPage(1); setFilters({ ...filters, identifier_type: v === "all" ? "" : v }); }}
          >
            <SelectTrigger data-testid="filter-type"><SelectValue placeholder="Tip" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate tipurile</SelectItem>
              {TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-slate-500">Se încarcă...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Tags className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              Nu există aliasuri pentru filtrele selectate.
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent</TableHead>
                    <TableHead>Agenție</TableHead>
                    <TableHead>Sursă</TableHead>
                    <TableHead>Tip</TableHead>
                    <TableHead>Valoare brută</TableHead>
                    <TableHead>Normalizat</TableHead>
                    <TableHead className="text-center">Primar</TableHead>
                    <TableHead className="text-right">Acțiuni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((m) => (
                    <TableRow key={m.id} data-testid={`mapping-row-${m.id}`}>
                      <TableCell>
                        <div className="font-medium">{m.agent_full_name || "—"}</div>
                        {m.agent_primary_marca && (
                          <Badge variant="outline" className="font-mono mt-1 text-[10px]">{m.agent_primary_marca}</Badge>
                        )}
                        {!m.agent_active && <Badge className="bg-slate-100 text-slate-500 ml-1 text-[10px]">inactiv</Badge>}
                      </TableCell>
                      <TableCell className="text-sm">{m.agency_name || <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px] uppercase">{m.source_system}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{m.identifier_type}</TableCell>
                      <TableCell className="font-mono text-sm">{m.raw_value}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">{m.normalized_value}</TableCell>
                      <TableCell className="text-center">
                        {m.is_primary ? <Badge className="bg-blue-50 text-blue-700">primar</Badge> : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {canManage && (
                          <Button variant="ghost" size="icon" onClick={() => setDeleteDialog(m)} data-testid={`delete-mapping-${m.id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {/* Pagination */}
              <div className="flex items-center justify-between border-t px-4 py-3 text-sm text-slate-600">
                <div data-testid="mappings-total">Total: <span className="font-medium">{total}</span></div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="page-prev">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span>Pagina {page} / {pages}</span>
                  <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="page-next">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alias Nou</DialogTitle>
            <DialogDescription>
              Adaugă un identificator (cod sau nume) folosit la matching automat. Idempotent pe (sursă, tip, valoare normalizată).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Agent *</Label>
              <Select value={form.agent_id || ""} onValueChange={(v) => setForm({ ...form, agent_id: v })}>
                <SelectTrigger data-testid="form-agent-id"><SelectValue placeholder="— alege agent —" /></SelectTrigger>
                <SelectContent>
                  {agents.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.full_name || a.id}{a.primary_marca_code ? ` (${a.primary_marca_code})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Sursă *</Label>
                <Select value={form.source_system} onValueChange={(v) => setForm({ ...form, source_system: v })}>
                  <SelectTrigger data-testid="form-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tip *</Label>
                <Select value={form.identifier_type} onValueChange={(v) => setForm({ ...form, identifier_type: v })}>
                  <SelectTrigger data-testid="form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Valoare brută *</Label>
              <Input
                value={form.raw_value}
                onChange={(e) => setForm({ ...form, raw_value: e.target.value })}
                placeholder={form.identifier_type === "marca_code" ? "ex: RE12917" : "ex: Popescu Ion"}
                data-testid="form-raw-value"
              />
              <p className="text-xs text-slate-500 mt-1">Va fi normalizată automat (uppercase pentru cod, lowercase fără diacritice pentru nume).</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.is_primary}
                onChange={(e) => setForm({ ...form, is_primary: e.target.checked })}
                data-testid="form-is-primary"
              />
              <Label>Marcare ca alias primar</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Anulează</Button>
            <Button onClick={handleCreate} data-testid="save-mapping-btn">Creează</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmare ștergere alias</DialogTitle>
            <DialogDescription>
              {deleteDialog && (
                <span>
                  Ștergi aliasul <span className="font-mono">{deleteDialog.raw_value}</span> ({deleteDialog.source_system}/{deleteDialog.identifier_type}) pentru <strong>{deleteDialog.agent_full_name}</strong>?
                  Importurile viitoare cu această valoare vor cădea înapoi în reconciliere.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>Anulează</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-mapping">Șterge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgentMappings;
